'use client';

import Link from 'next/link';
import { Shield, Lock, FileKey2, Server } from 'lucide-react';

export function SecurityCompliance() {
  return (
    <section className="py-20 bg-background border-t border-border">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Enterprise-Grade Security</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Designed to support 21 CFR Part 11 and EU Annex 11 controls. Your organization owns the validation; we provide the secure infrastructure.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {/* Pillar 1: Data Sovereignty */}
          <div className="p-6 rounded-2xl border bg-muted/10 hover:bg-muted/20 transition-colors">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
              <Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Swiss Data Residency</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Customer data is hosted securely in Zurich, Switzerland, ensuring strict EU-adequate data protection and privacy standards.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">GDPR Ready</span>
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">Swiss Hosting</span>
            </div>
          </div>

          {/* Pillar 2: Security Infrastructure */}
          <div className="p-6 rounded-2xl border bg-muted/10 hover:bg-muted/20 transition-colors">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Encryption & Access</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              TLS encryption in transit and provider-managed encryption at rest. Role-based access control (RBAC) ensures least-privilege access.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">TLS 1.3</span>
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">RBAC</span>
            </div>
          </div>

          {/* Pillar 3: Validation Support */}
          <div className="p-6 rounded-2xl border bg-muted/10 hover:bg-muted/20 transition-colors">
            <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
              <FileKey2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Audit Readiness</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Built-in audit trails log critical events. Exportable data ensures you are never locked in and can always prove compliance.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">Audit Trails</span>
              <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-background border">Full Export</span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link 
            href="/contact?topic=compliance" 
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <Shield className="w-4 h-4" />
            Contact our security team for technical documentation
          </Link>
        </div>
      </div>
    </section>
  );
}
